/**
 * Schedule resolution (doc 09 §6) — pure, shared by the gateway's slot
 * algorithm and the portal's overrides grid so both agree on what a resource
 * actually works on a given date. No I/O, no timezone parameter: a YYYY-MM-DD
 * calendar date maps to exactly one weekday everywhere.
 */
/** JS `getDay()` weekday key (0=Sun … 6=Sat) for a YYYY-MM-DD calendar date. */
export function weekdayKey(localDate) {
    const [y, m, d] = localDate.split('-').map(Number);
    // Midday UTC keeps the calendar date stable; a date's weekday is tz-independent.
    return String(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12)).getUTCDay());
}
/** Look up a weekday's intervals from a schedule's `hours` map (undefined → []). */
function dayHours(hours, localDate) {
    return hours[weekdayKey(localDate)] ?? [];
}
/**
 * The open intervals for ONE bookable resource on ONE local date (doc 09 §6).
 *
 * `staff` null = the business's implicit resource: either an unstaffed business,
 * or a staff member inheriting business hours (`schedule == null`). Two-level
 * fallback, NOT intersection:
 *
 *  1. A full-day business closure (empty business exception) closes everyone.
 *  2. A staff member with a personal schedule follows it outright — a personal
 *     exception (which may be empty = day off) overrides their weekly hours;
 *     a NON-empty business exception (special short day) does NOT apply to them.
 *  3. A staff member without a schedule (or the unstaffed business) follows
 *     business hours, including special short-day business exceptions.
 */
export function effectiveIntervals(staff, business, localDate) {
    const bizException = business.exceptions[localDate];
    if (bizException !== undefined && bizException.length === 0)
        return []; // (1)
    const schedule = staff?.schedule ?? null;
    if (schedule) {
        const own = schedule.exceptions[localDate];
        if (own !== undefined)
            return own; // (2) personal override (possibly a day off)
        return dayHours(schedule.hours, localDate);
    }
    if (bizException !== undefined)
        return bizException; // (3) special short day
    return dayHours(business.hours, localDate);
}
/**
 * The span an all-day service occupies on a day (doc 45): from the first open
 * minute to the last, breaks included — "the whole day" means the day, not
 * each of its shifts. `null` on a closed day.
 */
export function allDaySpan(intervals) {
    if (intervals.length === 0)
        return null;
    return {
        startMin: Math.min(...intervals.map((iv) => iv.startMin)),
        endMin: Math.max(...intervals.map((iv) => iv.endMin)),
    };
}
/**
 * Whether a resource's open intervals hold the booking [startMin, endMin) — the
 * owner's bookings in a staffed business (doc 85). A timed booking fits inside
 * ONE interval: it does not run through a break, the same rule the assistant's
 * slots follow. An all-day booking (doc 45) is measured against the day's span,
 * breaks included, because that is the time it is offered at.
 */
export function hoursCover(intervals, startMin, endMin, allDay = false) {
    if (allDay) {
        const span = allDaySpan(intervals);
        return span !== null && span.startMin <= startMin && span.endMin >= endMin;
    }
    return intervals.some((iv) => iv.startMin <= startMin && iv.endMin >= endMin);
}
/**
 * Granularities an owner may choose for their booking grid (doc 29).
 *
 * The schema allows 5–120; this is the shortlist the picker offers, which is a
 * product decision rather than a technical bound. A ten-minute grid lets a
 * caller take 10:10 rather than being rounded to 10:00 — worth it for a quick
 * service, ruinous for a two-hour one, which is why it is the owner's call.
 */
export const SLOT_GRANULARITY_OPTIONS = [10, 15, 20, 30, 45, 60, 90];
/**
 * Collapse "HH:mm" starts into contiguous ranges: `["09:00", "09:30", "10:00",
 * "14:00"]` at a 30-minute grid → `["09:00-10:00", "14:00"]`.
 *
 * Exists because of how the assistant sounded without it. A free day is up to
 * 48 starts, and it read every one of them aloud — a caller waiting through
 * "nine, half past nine, ten, half past ten…" has stopped listening long before
 * the time they wanted. Ranges say the same thing in one breath, and the model
 * still receives the full list, so any start inside a range can be booked.
 *
 * A range's end is the LAST BOOKABLE start, not a closing time: "09:00-10:00"
 * on a 30-minute grid means 09:00, 09:30 and 10:00 are free. Saying it the
 * other way would have the assistant offer a time the tool never returned.
 *
 * Adjacency is measured on the grid the slots were computed with, so a gap of
 * exactly one step is a real gap (something is booked there) and breaks the
 * range — which is the whole point of grouping rather than printing first-last.
 */
export function groupSlotRanges(labels, stepMin) {
    const step = Math.max(1, Math.round(stepMin));
    const mins = labels
        .map((l) => {
        const [h, m] = l.split(':');
        const minutes = Number(h) * 60 + Number(m);
        return Number.isFinite(minutes) ? minutes : null;
    })
        .filter((v) => v !== null)
        .sort((a, b) => a - b);
    if (mins.length === 0)
        return [];
    const label = (v) => `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
    const out = [];
    let start = mins[0];
    let prev = mins[0];
    for (const cur of mins.slice(1)) {
        if (cur - prev === step) {
            prev = cur;
            continue;
        }
        out.push(start === prev ? label(start) : `${label(start)}-${label(prev)}`);
        start = cur;
        prev = cur;
    }
    out.push(start === prev ? label(start) : `${label(start)}-${label(prev)}`);
    return out;
}
//# sourceMappingURL=schedule.js.map